// @ts-strict-ignore
import React, { useRef } from 'react';
import type { Action, NodeEditorTab } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { UseFormProps } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { FormControlMap } from './types';
import { getFormAdapter, getActionTabKey } from './helpers';
import { useAppSelector } from '~/store/reduxHooks';
import {
  selectWorkflowProps, selectPublishedAttributes,
} from '~/dashboard/views/workflows/store/redux/selectors';

import type { FormState as InputAttributeConfigurationFormState } from '../AttributeConfiguration/InputAttributeConfigurationTab';
import type { FormState as MatrixOptionsFormState } from '../MatrixOptionsTab';
import type { FormState as OutputAttributeExpressionFormState } from '../OutputAttributeExpressionTab';
import type { FormState as OutputAttributeOptionsFormState } from '../AttributeConfiguration/OutputAttributeOptionsTab';
import type { FormState as ServiceLocalSettingsFormState } from '../ServiceTabs/ServiceLocalSettingsTab';
import type { FormState as TagGlobalSettingsFormState } from '../TagGlobalSettingsTab';
import type { FormState as TagLocalSettingsFormState } from '../TagLocalSettingsTab';

/**
 * Handles all of the form creation logic for all NodeEditorDrawer forms.
 * This hook also deals with resetting a form to its default values whenever a node is focused.
 *
 * @param focusedAction action for which to load a form controller
 * @param tab tab for which to load a form controller
 * @param extraFormProps - Anything you can pass to react-hook-form's `useForm`
 * @returns a form controller for the specified action+tab combination with full type safety.
 */
export default function useNodeEditorForm(
  focusedAction: Action,
  tab: NodeEditorTab,
  extraFormProps?: UseFormProps<any>,
) {
  const workflowProps = useAppSelector(selectWorkflowProps);
  const publishedAttributes = useAppSelector(selectPublishedAttributes);

  // define custom react hook form props
  const formProps: UseFormProps<any> = {
    mode: 'onTouched',
    ...extraFormProps,
  };
  // props are selected here to keep form control in sync with the store
  /**
   * This map indexes an action type + tab combination with a corresponding form controller.
   * A node can have more than one form, as it might have more than one drawer tab with a
   * form in it.
   */
  const formControlMap: FormControlMap = {
    [getActionTabKey(ActionType.INPUT, 'configuration')]: useForm<InputAttributeConfigurationFormState>(formProps),
    [getActionTabKey(ActionType.MATRIX_MODEL, 'options')]: useForm<MatrixOptionsFormState>(formProps),
    [getActionTabKey(ActionType.OUTPUT, 'options')]: useForm<OutputAttributeOptionsFormState>(formProps),
    [getActionTabKey(ActionType.OUTPUT, 'rule')]: useForm<OutputAttributeExpressionFormState>(formProps),
    [getActionTabKey(ActionType.SERVICE, 'configuration')]: useForm<ServiceLocalSettingsFormState>(formProps),
    [getActionTabKey(ActionType.TAG, 'configuration')]: useForm<TagGlobalSettingsFormState>(formProps),
    [getActionTabKey(ActionType.TAG, 'options')]: useForm<TagLocalSettingsFormState>(formProps),
    // add other form controllers here
  };

  const formKey = getActionTabKey(focusedAction?.type, tab);
  const formControl = formControlMap[formKey];

  const prevFormResetKey = useRef<string>('');

  /**
   * Calls the `reset()` function on the corresponding form controller when reset conditions are met:
   * - new node is focused
   * - new tab is opened
   * - the focused action data changed through a form submission
   * - the focused action data changed from Redux/source externally (not through form submission)
   * - PAs have changed (race condition)
   * - undo/redo moved the history cursor
   * This populates the forms with the specific values returned from each instance of
   * the default values function for the given form adapter.
   */
  React.useEffect(() => {
    if (!focusedAction) {
      prevFormResetKey.current = '';
      return;
    }
    if (!formControl) return;

    const formAdapter = getFormAdapter(focusedAction, tab);
    const sourceDefaultFormValues = formAdapter.getDefaultFormValues(focusedAction, workflowProps);
    /**
     * if opted in, syncs the form with the data from the store.
     * this covers the cases where the data behind the form is modified outside of the
     * life-cycle of the form. Otherwise, forms could show stale data compared to the store
     * for example:
     * - an Action in redux feeds the form.
     * - a dispatch (but not the form submission) changes the Action
     * - without the explicit sync, the form would show the Action data pre-dispatch
     * - the form should be updated to reflect the new Action data
     */
    const sourceSyncKey = formAdapter.shouldSyncFormFromSource?.(focusedAction)
      ? formAdapter.getSourceSyncKey?.({
        action: focusedAction,
        sourceDefaultFormValues,
        workflowProps,
      },
      ) ?? sourceDefaultFormValues
      : undefined;

    const currentResetKey = getFormResetKey({
      action: focusedAction,
      drawerTab: tab,
      paCount: publishedAttributes.length,
      sourceSyncKey,
    });
    const hasAnyDefinedFormValue = (
      Object.values(formControl?.getValues() ?? {}).some(value => value !== undefined)
    );
    const resetKeyChanged = prevFormResetKey.current !== currentResetKey;
    const shouldReset = resetKeyChanged || !hasAnyDefinedFormValue;

    if (shouldReset) {
      formControl.reset(sourceDefaultFormValues);
      prevFormResetKey.current = currentResetKey;
    }
  },
  [focusedAction, formControl, publishedAttributes, tab, workflowProps]);

  return formControl;
}


function getFormResetKey(
  args: {
    action: Action;
    drawerTab: NodeEditorTab;
    paCount: number;
    sourceSyncKey: unknown;
  },
): string {
  const {
    action, drawerTab, paCount, sourceSyncKey,
  } = args;

  if (!action) return '';
  return JSON.stringify({
    // focused node changed
    publicId: action.publicId,
    // tab changed (same node)
    drawerTab,
    // values changed (same node)
    tagOutcomeKeys: (action.tag?.outcomes ?? []).map(o => o.outcome).sort().join(','),
    hasPA: Boolean(action.input?.publishedAttributeToken
      || action.output?.publishedAttributeToken),
    paCount,
    sourceSyncKey,
  });
}
